import _ from 'lodash';
import { Observable } from 'rxjs';
import { startSubmit, stopSubmit, initialize } from 'redux-form/immutable';
import { replace, push } from 'react-router-redux';
import type { Store } from 'redux';
import type { ActionsObservable } from 'redux-observable';

import BudgetApi from 'entities/budget/api';
import CauseApi from 'entities/cause/api';
import { selectBudgets } from 'entities/budget/selectors';
import LocationsApi from 'entities/location/api';
import DonationCreditRuleApi from 'entities/donationCreditRule/api';
import DonationCreditRule from 'entities/donationCreditRule/model';
import { selectDonationCreditRuleById } from 'entities/donationCreditRule/selectors';
import { selectUserLocations } from 'entities/user/selectors';
import { showMessageAction, showErrorAction } from 'containers/Notification/actions';
import { selectCauses } from 'entities/cause/selectors';
import type { State } from 'reducers';

import { FORM_IDENTIFIER, INIT, SAVE, REMOVE } from './constants';
import {
  initSuccessAction,
  initFailedAction,
  saveSuccessAction,
  saveFailedAction,
  removeSuccessAction,
  removeFailedAction,
} from './actions';

const init = (
  action$: ActionsObservable<{ id: string; budgetId: string; type: typeof INIT }>,
  store: Store<State>
) =>
  action$.ofType(INIT).mergeMap(({ id, budgetId }) => {
    const state = store.getState();
    const actions: Observable<any>[] = [BudgetApi.fetchAll()];

    const causes = selectCauses(state);
    if (causes.size === 0) {
      actions.push(CauseApi.fetchAll());
    }

    const locations = selectUserLocations(state);
    if (locations.size === 0) {
      actions.push(LocationsApi.fetchAll());
    }

    if (id === 'new') {
      const budgets = selectBudgets(state);
      const budget = budgets.get(budgetId);

      actions.push(
        Observable.of(
          initialize(
            FORM_IDENTIFIER,
            new DonationCreditRule({
              budget: budgetId,
              currencyCode: budget?.get('currencyCode') || '',
            }).toForm()
          )
        )
      );
    } else {
      const donationCreditRule = selectDonationCreditRuleById(state, id);
      if (donationCreditRule) {
        actions.push(Observable.of(initialize(FORM_IDENTIFIER, donationCreditRule.toForm())));
      } else {
        actions.push(
          DonationCreditRuleApi.fetch(id)
            .map((action) => [
              action,
              initialize(FORM_IDENTIFIER, action.donationCreditRule?.toForm()),
            ])
            .catch((e) => Observable.of(initFailedAction(e), replace('/notFound')))
        );
      }
    }

    return Observable.combineLatest(actions)
      .mergeMap((resultingActions) => [..._.flatten(resultingActions), initSuccessAction()])
      .catch((e) => Observable.of(initFailedAction(e)));
  });

const save = (action$: ActionsObservable<{ type: typeof SAVE; data: { id: string } }>) =>
  action$.ofType(SAVE).mergeMap((payload) =>
    Observable.concat(
      Observable.of(startSubmit(FORM_IDENTIFIER)),
      Observable.from(
        payload.data.id
          ? DonationCreditRuleApi.update(payload.data.id, payload.data)
          : DonationCreditRuleApi.create(payload.data)
      )
        .mergeMap((action) => [
          action,
          saveSuccessAction(FORM_IDENTIFIER),
          stopSubmit(FORM_IDENTIFIER),
          initialize(FORM_IDENTIFIER, action.donationCreditRule.toJS()),
          push('/donations/rules'),
          showMessageAction(payload.data.id ? 'Changes Saved!' : 'Donation Credit Rule Created!'),
        ])
        .catch((e) =>
          Observable.of(
            saveFailedAction(e, 'saveFailed'),
            stopSubmit(FORM_IDENTIFIER),
            showErrorAction('An error occurred during saving, please try again.')
          )
        )
    )
  );

const remove = (
  action$: ActionsObservable<{ type: typeof REMOVE; donationCreditRuleId: string }>
) =>
  action$.ofType(REMOVE).mergeMap((payload) =>
    Observable.from(DonationCreditRuleApi.delete(payload.donationCreditRuleId))
      .mergeMap((action) => [
        action,
        removeSuccessAction(),
        push('/donations/rules'),
        showMessageAction('Donation Credit Rule Removed!'),
      ])
      .catch((e) =>
        Observable.of(
          removeFailedAction(e, 'removeFailed'),
          stopSubmit(FORM_IDENTIFIER),
          showErrorAction('An error occurred during saving, please try again.')
        )
      )
  );

export default [init, save, remove];
